import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteMenuItemMutation,
  useUpdateAvailabilityStatusMutation,
} from "../../../../../redux/services/menuItemService";
import { MenuItem, setAllMenuItemsRowsPerPage } from "../../../../../redux/menuSlice";
import { useFirebaseMenuItemsList } from "../../../../../hooks/useFirebaseMenu";
import Swal from "sweetalert2";
import DataTable, { TableColumn } from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faSpinner,
  faTrash,
  faSearch,
  faSliders,
  faArchive,
} from "@fortawesome/free-solid-svg-icons";
import { Button, InputGroup } from "react-bootstrap";
import "./index.css";
import ToggleSwitch from "../../../../../components/ToggleSwitch";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { formatFirestoreTimestamp, getImageUrlWithFallback } from "../../../../../utility/Utility";
import { DEFAULT_IMAGE_URL } from "../../../../../constants/constans";
import IconViewComponent from "../../../../../components/IconViewComponent";
import TableLoadingComponent from "../../../../../components/TableLoadingComponent";
import { Input, InputGroupText } from "reactstrap";
import React from "react";
import { useRestaurantDetails } from "../../../../../hooks/useFirebaseOrders";

export const useMenuItems = () => {
  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const { data: menuItemsData, loading: isLoading } = useFirebaseMenuItemsList(restaurantId);

  const items = menuItemsData?.menuItems || [];

  return { items, isLoading, restaurantId };
};

export const useMenuItemActions = (restaurantId: string) => {
  const [deletingMenuId, setDeletingMenuId] = useState<string | null>(null);
  const [archivingMenuId, setArchivingMenuId] = useState<string | null>(null);

  const [deleteMenuItem] = useDeleteMenuItemMutation();
  const [updateAvailabilityStatus] = useUpdateAvailabilityStatusMutation();

  const handleDeleteClick = async (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#590B21",
      cancelButtonColor: "#ddd",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        setDeletingMenuId(id);
        try {
          await deleteMenuItem({ menuId: id, restId: restaurantId });
          Swal.fire("Deleted!", "Your file has been deleted.", "success");
          setDeletingMenuId(null);
        } catch (error) {
          console.error("Delete Error:", error);
          Swal.fire(
            "Error!",
            "An error occurred while deleting the item.",
            "error"
          );
          setDeletingMenuId(null);
        }
      }
    });
  };

  const handleToggleChange = async (menuId: string, status: boolean) => {
    try {
      await updateAvailabilityStatus({
        restaurantId,
        menuId,
        menuType: "Single",
        status
      }).unwrap();
    } catch (error) {
      console.error("Error updating availability status:", error);
      toast.error("Failed to update availability.");
    }
  };

  const handleArchiveClick = async (id: string) => {
    Swal.fire({
      title: "Archive Item?",
      text: "This item will be archived and hidden from the menu.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#590B21",
      cancelButtonColor: "#ddd",
      confirmButtonText: "Yes, archive it!",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        setArchivingMenuId(id);
        try {
          await updateAvailabilityStatus({
            restaurantId,
            menuId: id,
            menuType: "Single",
            status: false,
            isArchived: true
          }).unwrap();
          Swal.fire("Archived!", "The item has been archived.", "success");
          setArchivingMenuId(null);
        } catch (error) {
          console.error("Archive Error:", error);
          Swal.fire(
            "Error!",
            "An error occurred while archiving the item.",
            "error"
          );
          setArchivingMenuId(null);
        }
      }
    });
  };

  return { deletingMenuId, archivingMenuId, handleDeleteClick, handleToggleChange, handleArchiveClick };
};

const AllMenuItems = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { items, isLoading, restaurantId } = useMenuItems();
  const { deletingMenuId, archivingMenuId, handleDeleteClick, handleToggleChange, handleArchiveClick } =
    useMenuItemActions(restaurantId);
  const { data: restaurantData, loading: isFilterTagsLoading } = useRestaurantDetails(restaurantId);
  const fetchedFilterTagsData = restaurantData?.filterTags || null;

  // Local search state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilterTags, setSelectedFilterTags] = useState<string[]>([]);
  const [filterTagsData, setFilterTagsData] = useState<any>(null);

  React.useEffect(() => {
    setFilterTagsData(fetchedFilterTagsData || null);
  }, [fetchedFilterTagsData]);

  const handleAllFilter = () => {
    setSelectedFilterTags([]);
  };

  const handleTagFilter = (tagObj: any, groupData: any) => {
    const isSelected = selectedFilterTags.includes(tagObj.tag);
    const allowMultiple =
      groupData && typeof groupData === "object" && "allowMultipleSelect" in groupData
        ? groupData.allowMultipleSelect
        : false;

    setSelectedFilterTags((prevTags) => {
      if (allowMultiple) {
        return isSelected
          ? prevTags.filter((tag) => tag !== tagObj.tag)
          : [...prevTags, tagObj.tag];
      }

      const tags = groupData && groupData.tags && Array.isArray(groupData.tags) ? groupData.tags : [];
      const otherGroupTags = tags.map((t: any) => t.tag).filter((t: string) => t !== tagObj.tag);
      const filteredTags = prevTags.filter((tag) => !otherGroupTags.includes(tag));

      if (isSelected) {
        return filteredTags.filter((tag) => tag !== tagObj.tag);
      }

      return [...filteredTags, tagObj.tag];
    });
  };

  const filterBySelectedTag = (list: MenuItem[]): MenuItem[] => {
    if (selectedFilterTags.length === 0 || !filterTagsData) {
      return list;
    }

    const normalizedSelectedTags = selectedFilterTags.map((tag) => tag.toLowerCase());
    const selectedGroupMap: Record<string, Set<string>> = {};
    const groupKeyMapping: Record<string, string> = {
      Brand: "brand",
      Class: "class",
      Cuisine: "cuisine",
      "New Arrivals": "new arrivals",
    };

    for (const [groupName, groupData] of Object.entries(filterTagsData)) {
      if (groupData && typeof groupData === "object" && "tags" in groupData) {
        const tags = (groupData as any).tags;
        if (Array.isArray(tags)) {
          for (const tag of normalizedSelectedTags) {
            if (tags.some((t: any) => t.tag && t.tag.toLowerCase() === tag)) {
              const itemKey = groupKeyMapping[groupName] || groupName.toLowerCase();
              if (!selectedGroupMap[itemKey]) {
                selectedGroupMap[itemKey] = new Set();
              }
              selectedGroupMap[itemKey].add(tag);
            }
          }
        }
      }
    }

    return list.filter((item) => {
      const itemTags = (item as any).tags;

      if (!itemTags || typeof itemTags !== "object") {
        return false;
      }

      for (const [itemKey, selectedTagsInGroup] of Object.entries(selectedGroupMap)) {
        const itemTagsForGroup = itemTags[itemKey] || [];

        if (Array.isArray(itemTagsForGroup)) {
          const normalizedItemTags = itemTagsForGroup.map((tag: any) =>
            typeof tag === "string" ? tag.toLowerCase() : ""
          );
          const hasMatch = Array.from(selectedTagsInGroup).some((selectedTag) =>
            normalizedItemTags.includes(selectedTag)
          );
          if (!hasMatch) {
            return false;
          }
        } else if (typeof itemTagsForGroup === "string") {
          const hasMatch = Array.from(selectedTagsInGroup).includes(itemTagsForGroup.toLowerCase());
          if (!hasMatch) {
            return false;
          }
        } else {
          return false;
        }
      }
      return true;
    });
  };

  // Filter items based on search term (flexible search - words can be in any order)
  const searchFilteredItems = React.useMemo(() => {
    if (searchTerm.trim() === "") {
      return items.filter((item: MenuItem) => !(item as any).isArchived);
    }
    const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
    return items.filter((item: MenuItem) => {
      if ((item as any).isArchived) return false;
      
      const searchableText = [
        item.displayName || "",
        (item as any).itemType || "",
        String((item as any).tinyId || ""),
        item.productId || "",
      ]
        .join(" ")
        .toLowerCase();

      return searchWords.every((word) => searchableText.includes(word));
    });
  }, [items, searchTerm]);

  const filteredItems = React.useMemo(() => {
    return filterBySelectedTag(searchFilteredItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilteredItems, selectedFilterTags, filterTagsData]);

  const handleEditClick = (item: MenuItem) => {
    navigate("/productCatalogue/stock-keeping-unit/update-product", {
      state: { menuItem: item },
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const rowsPerPage = useSelector((state: any) => state.menu.allMenuItemsRowsPerPage);

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setAllMenuItemsRowsPerPage(newRowsPerPage));
  };

  const columns: TableColumn<MenuItem>[] = [
    {
      name: <h6 className="fw-bold">Image</h6>,
      selector: (row: MenuItem) => row.imageUrl,
      cell: (row: { imageUrl: any; displayName: string | undefined }) => (
        <img
          src={row.imageUrl.length > 0 ? getImageUrlWithFallback(row.imageUrl[0], DEFAULT_IMAGE_URL, 64) : DEFAULT_IMAGE_URL}
          alt={row.displayName || ""}
          className="menuitem-image"
        />
      ),
    },
    {
      name: <h6 className="fw-bold">Name</h6>,
      cell: (row: MenuItem) => (
        <div>
          <div>{row.displayName}</div>
          {row?.isCustomMenuItem && <IconViewComponent />}
        </div>
      ),
    },
    {
      name: <h6 className="fw-bold">Product Id</h6>,
      selector: (row: MenuItem) => row.productId || "",
    },
    {
      name: <h6 className="fw-bold">Price(Rs.)</h6>,
      selector: (row: MenuItem) => row.price,
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      selector: (row: MenuItem) => formatFirestoreTimestamp(row.createdAt),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Updated Date</h6>,
      selector: (row: MenuItem) => formatFirestoreTimestamp(row.updatedAt),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Availability</h6>,
      cell: (row: MenuItem) => (
        <ToggleSwitch
          initialChecked={row.isAvailable}
          menuId={row.id}
          onChange={handleToggleChange}
        />
      ),
    },
    {
      name: <h6 className="fw-bold">Actions</h6>,
      cell: (row: MenuItem) => (
        <div className="menuitem-action-buttons">
          <Button variant="light" onClick={() => handleEditClick(row)}>
            <FontAwesomeIcon
              icon={faEye}
              title="Edit"
              className="menuitem-edit-button"
            />
          </Button>

          {archivingMenuId === row.id ? (
            <Button variant="light" disabled={true}>
              <FontAwesomeIcon icon={faSpinner} spin />
            </Button>
          ) : (
            <Button variant="light" onClick={() => handleArchiveClick(row.id)}>
              <FontAwesomeIcon
                icon={faArchive}
                title="Archive"
                className="menuitem-archive-button"
              />
            </Button>
          )}

          {deletingMenuId === row.id ? (
            <Button variant="light" disabled={true}>
              <FontAwesomeIcon icon={faSpinner} spin />
            </Button>
          ) : (
            <Button variant="light" onClick={() => handleDeleteClick(row.id)}>
              <FontAwesomeIcon
                icon={faTrash}
                title="Delete"
                className="menuitem-delete-button"
              />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading ? (
        <div className="py-2">
          <TableLoadingComponent numColumns={5} />
        </div>
      ) : (
        <>
          {/* Search Input */}
          <div className="p-3 bg-white">
            <InputGroup>
              <InputGroupText>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroupText>
              <Input
                type="text"
                placeholder="Search menu items by name or SKU ID..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
            </InputGroup>

            {isFilterTagsLoading ? (
              <div className="filter-tags-container mt-3">
                <div className="d-flex align-items-center mb-3">
                  <FontAwesomeIcon icon={faSliders} className="me-2" />
                  <h6 className="mb-0 fw-bold">Filter Items</h6>
                </div>

                <div className="filter-tags-scroll-container">
                  <div className="filter-tags-wrapper">
                    {[...Array(8)].map((_, index) => (
                      <div
                        key={`skeleton-${index}`}
                        className="filter-tag-skeleton"
                        style={{
                          width: `${60 + Math.random() * 40}px`,
                          height: "32px",
                          borderRadius: "16px",
                          margin: "0 8px 8px 0",
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              filterTagsData &&
              Object.keys(filterTagsData).length > 0 && (
                <div className="filter-tags-container mt-3">
                  <div className="d-flex align-items-center mb-3">
                    <FontAwesomeIcon icon={faSliders} className="me-2" />
                    <h6 className="mb-0 fw-bold">Filter Items</h6>
                  </div>

                  <div className="filter-tags-scroll-container">
                    <div className="filter-tags-wrapper">
                      <button
                        className={`filter-tag-btn ${selectedFilterTags.length === 0 ? "active" : ""}`}
                        onClick={handleAllFilter}
                      >
                        All
                      </button>

                      {Object.entries(filterTagsData).map(
                        ([category, categoryData]: [string, any], categoryIndex: number) => {
                          const visibleTags = categoryData.tags?.filter((tag: any) => tag.show) || [];

                          if (visibleTags.length === 0) return null;

                          return (
                            <React.Fragment key={category}>
                              {categoryIndex > 0 && <div className="filter-category-separator" />}

                              {visibleTags.map((tagObj: any, index: number) => {
                                const isSelected = selectedFilterTags.includes(tagObj.tag);

                                return (
                                  <button
                                    key={`${category}-${tagObj.tag}-${index}`}
                                    className={`filter-tag-btn ${isSelected ? "active" : ""}`}
                                    onClick={() => handleTagFilter(tagObj, categoryData)}
                                  >
                                    {tagObj.tag}
                                  </button>
                                );
                              })}
                            </React.Fragment>
                          );
                        }
                      )}
                    </div>
                  </div>

                  {selectedFilterTags.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">
                        {selectedFilterTags.length} filter
                        {selectedFilterTags.length !== 1 ? "s" : ""} applied
                      </small>
                    </div>
                  )}
                </div>
              )
            )}
          </div>

          <DataTable
            responsive
            columns={columns}
            data={filteredItems}
            pagination
            highlightOnHover
            paginationPerPage={rowsPerPage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            noDataComponent={
              <div className="py-4 text-center">
                {searchTerm
                  ? `No menu items found for "${searchTerm}"`
                  : selectedFilterTags.length > 0
                    ? "No menu items match selected filters"
                    : "No menu items available"}
              </div>
            }
          />
        </>
      )}
    </>
  );
};

export default AllMenuItems;